import { isToday as dateFnsIsToday, isTomorrow as dateFnsIsTomorrow, isPast } from 'date-fns';
import { GameType, Pick, TicketType } from './contexts/LottoContext';

const WEEK_DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const dateToStringFull = (date: Date): string =>
  `${WEEK_DAY[date.getDay()]}, ${MONTH[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

export const dateToString = (date: Date, hideYear?: boolean): string => {
  let dateString = `${WEEK_DAY[date.getDay()]}, ${MONTH[date.getMonth()]} ${date.getDate()}`;
  if (!hideYear) dateString += `, ${date.getFullYear()}`;
  if (isToday(date)) dateString = `Today ${dateString}`;
  if (isTomorrow(date)) dateString = `Tomorrow ${dateString}`;
  return dateString;
};

export function formatRelativeDate(date: Date, hideYear?: boolean): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  } else {
    let dateString = `${WEEK_DAY[date.getDay()]}, ${MONTH[date.getMonth()]} ${date.getDate()}`;
    if (!hideYear) dateString += `, ${date.getFullYear()}`;
    return dateString;
  }
}

export const areTheSameDates = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const isToday = (date: Date): boolean => dateFnsIsToday(date);

export const isTomorrow = (date: Date): boolean => dateFnsIsTomorrow(date);

export const isTomorrowOrLater = (date: Date, compareDate?: Date): boolean => {
  const now = compareDate || new Date();
  const [nowYear, nowMonth, nowDay] = [now.getFullYear(), now.getMonth(), now.getDate()];
  const tomorrow = new Date(nowYear, nowMonth, nowDay + 1);
  return date >= tomorrow;
};

export const isPastDate = (date: Date): boolean => isPast(date);

export const hasPast = (date: Date, compareDate?: Date): boolean => {
  if (!compareDate) compareDate = new Date();
  return date.getTime() < compareDate.getTime();
};

export const pad = (num: any): string => `${num}`.padStart(2, '0');

export const dateToStringValues = (date: Date): string =>
  `${pad(date.getMonth() + 1).trim()}/${pad(date.getDate()).trim()}/${date.getFullYear()}`;

const timeToStringValues = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

export const datetimeToString = (date: Date): string =>
  `${dateToStringValues(date)} ${timeToStringValues(date)}`;

export const timeLeft = (date: Date, compareDate?: Date): string => {
  if (!compareDate) compareDate = new Date();
  const diff = date.getTime() - compareDate.getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours > 0) {
    if (hours < 24) {
      return `In ${hours}h`;
    } else {
      const days = Math.floor(hours / 24);
      return `In ${days} ${days > 1 ? 'days' : 'day'}`;
    }
  } else {
    const minutes = Math.floor(diff / 60000);
    if (minutes > 0) {
      return `In ${minutes}m`;
    } else {
      return `In <1m`;
    }
  }
};

export const minutesAndSecondsLeft = (date: Date, timeDifferenceInMinutes: number): string => {
  const now = new Date();
  const diff = date.getTime() - (now.getTime() + timeDifferenceInMinutes * 60000);
  let minutes = Math.floor(diff / 60000);
  let seconds = Math.round((diff / 60000 - minutes) * 60);
  if (seconds === 60) {
    minutes++;
    seconds = 0;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

export const timePast = (date: Date, compareDate?: Date): string => {
  if (!compareDate) compareDate = new Date();
  const diff = compareDate.getTime() - date.getTime();
  const months = Math.floor(diff / 3600000 / 24 / 30);
  if (months > 0) {
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else {
    const days = Math.floor(diff / 3600000 / 24);
    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      const hours = Math.floor(diff / 3600000);
      if (hours > 0) {
        return `${hours}h ago`;
      } else {
        const minutes = Math.floor(diff / 60000);
        if (minutes > 0) {
          return `${minutes}m ago`;
        } else return `Just now`;
      }
    }
  }
};

export const daysPast = (date: Date, compareDate?: Date): string => {
  if (!compareDate) compareDate = new Date();
  const dateRounded = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const compareDateRounded = new Date(
    compareDate.getFullYear(),
    compareDate.getMonth(),
    compareDate.getDate()
  );
  const diff = compareDateRounded.getTime() - dateRounded.getTime();
  const years = Math.floor(diff / 3600000 / 24 / 365);
  if (years > 1) {
    return 'Over a year ago';
  } else if (years === 1) {
    return 'Last year';
  } else {
    const months = Math.floor(diff / 3600000 / 24 / 30);
    if (months > 1) {
      return `${months} months ago`;
    } else if (months === 1) {
      return 'Last month';
    } else {
      const weeks = Math.floor(diff / 3600000 / 24 / 7);
      if (weeks > 1) {
        return `${weeks} weeks ago`;
      } else if (weeks === 1) {
        return 'Last week';
      } else {
        const days = Math.floor(diff / 3600000 / 24);
        if (days > 1) {
          return `${days} days ago`;
        } else if (days === 1) {
          return 'Yesterday';
        } else {
          return 'Today';
        }
      }
    }
  }
};

export const secondsToTimeString = (totalSecondsLeft: number): string => {
  const hours = Math.floor(totalSecondsLeft / 3600);
  const minutes = Math.floor((totalSecondsLeft % 3600) / 60);
  const seconds = totalSecondsLeft % 60;

  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');
  const secondsStr = seconds.toString().padStart(2, '0');

  return `${hoursStr}:${minutesStr}:${secondsStr}`;
};

export const secondsToTrimmedTimeString = (totalSecondsLeft: number): string => {
  const hours = Math.floor(totalSecondsLeft / 3600);
  const minutes = Math.floor((totalSecondsLeft % 3600) / 60);
  const seconds = totalSecondsLeft % 60;

  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');
  const secondsStr = seconds.toString().padStart(2, '0');

  return hours > 0
    ? `${hoursStr}:${minutesStr}:${secondsStr}`
    : minutes > 0
    ? `${minutesStr}:${secondsStr}`
    : secondsStr;
};

export const stringToDate = (dateString: string): Date => {
  const [date, time] = dateString.split(' ');
  const [month, day, year] = date.split('/');
  const [hours, minutes, seconds] = time ? time.split(':') : ['00', '00', '00'];
  return new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
    parseInt(seconds)
  );
};

export const dateStringToDate = (dateString: string): Date => {
  const [month, day, year] = dateString.split('/');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

export function resizeImage(
  file: File,
  maxwidth: number,
  maxheight: number,
  callback: (url: Blob) => void
) {
  const fileTracker = new FileReader();
  fileTracker.onload = function () {
    const image = new Image();
    image.onload = function () {
      const canvas = document.createElement('canvas');

      if (image.height > maxheight) {
        image.width *= maxheight / image.height;
        image.height = maxheight;
      }

      if (image.width > maxwidth) {
        image.height *= maxwidth / image.width;
        image.width = maxwidth;
      }
      const ctx = canvas.getContext('2d');
      ctx!.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = image.width;
      canvas.height = image.height;
      ctx!.drawImage(image, 0, 0, image.width, image.height);
      const dataURL = canvas.toDataURL('image/png');
      callback(dataURLToBlob(dataURL));
    };
    image.src = URL.createObjectURL(file);
  };
  fileTracker.readAsDataURL(file);
}

function dataURLToBlob(dataURL: string): Blob {
  const BASE64_MARKER = ';base64,';
  if (dataURL.indexOf(BASE64_MARKER) === -1) {
    const parts = dataURL.split(',');
    const contentType = parts[0].split(':')[1];
    const raw = parts[1];

    return new Blob([raw], { type: contentType });
  }

  const parts = dataURL.split(BASE64_MARKER);
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;

  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
}

export const isTouchEnabled = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export function filterOutDuplicates<T>(arr: T[]): T[] {
  const uniqueItems: Set<T> = new Set();
  const result: T[] = [];

  for (const item of arr) {
    if (!uniqueItems.has(item)) {
      uniqueItems.add(item);
      result.push(item);
    }
  }

  return result;
}

export enum REWARD_TYPES {
  Wheel = 'SpinWheel',
  None = 'None',
  FlatCashback = 'FlatCashback',
  TieredCashback = 'TieredCashback',
  Blackjack = 'Blackjack',
}

export enum CAMPAIGN_TYPES {
  DailyCounter = 'DailyCounter',
  AmountCounter = 'AmountCounter',
  PurchaseCounter = 'PurchaseCounter',
  MANUAL_AMOUNT = 'ManualAmount',
  PERCENTAGE_RANGE = 'PercentageRange',
  ActionerStreak = 'ActionerStreak',
  GiftCard = 'GiftCard',
  RewardBased = 'RewardBased',
  SpinKick = 'SpinKick',
  BlackjackBoost = 'BlackjackBoost',
  OnTheSpot = 'OnTheSpot',
}

export const getClassByCampaignType = (scaleType: string, campaignName: string) => {
  if (scaleType === CAMPAIGN_TYPES.DailyCounter) {
    return '';
  } else if (
    scaleType === CAMPAIGN_TYPES.AmountCounter &&
    campaignName !== 'T-day Campaign' &&
    campaignName !== 'Frenzy Friday'
  ) {
    return 'pumpkin';
  } else if (scaleType === CAMPAIGN_TYPES.AmountCounter && campaignName === 'T-day Campaign') {
    return 'tday';
  } else if (scaleType === CAMPAIGN_TYPES.AmountCounter && campaignName === 'Frenzy Friday') {
    return 'black-friday';
  } else {
    return '';
  }
};

export const getPickNumberFromPick = (pick: Pick): number => {
  switch (pick) {
    case Pick.PICK2:
      return 2;
    case Pick.PICK3:
      return 3;
    case Pick.PICK4:
      return 4;
    case Pick.PICK5:
      return 5;
    default:
      return 2;
  }
};

export const getPickFromPickNumber = (pickNumber: number): Pick => {
  switch (pickNumber) {
    case 2:
      return Pick.PICK2;
    case 3:
      return Pick.PICK3;
    case 4:
      return Pick.PICK4;
    case 5:
      return Pick.PICK5;
    default:
      return Pick.PICK2;
  }
};

export const shortPick = (gameType: Pick) => {
  switch (gameType) {
    case Pick.PICK2:
      return 'P2';
    case Pick.PICK3:
      return 'P3';
    case Pick.PICK4:
      return 'P4';
    case Pick.PICK5:
      return 'P5';
    default:
      return '';
  }
};

export const pickLabel = (gameType: Pick) => {
  switch (gameType) {
    case Pick.PICK2:
      return 'Pick 2';
    case Pick.PICK3:
      return 'Pick 3';
    case Pick.PICK4:
      return 'Pick 4';
    case Pick.PICK5:
      return 'Pick 5';
    default:
      return '';
  }
};

export const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
export const getShortDay = (day: string) => {
  const shortDaysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const index = daysOfWeek.indexOf(day);
  return index !== -1 ? shortDaysOfWeek[index] : '';
};

export enum TicketGameType {
  Pick2Straight = 'TicketPick2Straight',
  Pick2Boxed = 'TicketPick2Boxed',
  Pick3Straight = 'TicketPick3Straight',
  Pick3Boxed = 'TicketPick3Boxed',
  Pick4Straight = 'TicketPick4Straight',
  Pick4Boxed = 'TicketPick4Boxed',
  Pick5Straight = 'TicketPick5Straight',
  Pick5Boxed = 'TicketPick5Boxed',
}

enum TicketGameTypeShort {
  P2S = 'P2S',
  P2B = 'P2B',
  P3S = 'P3S',
  P3B = 'P3B',
  P4S = 'P4S',
  P4B = 'P4B',
  P5S = 'P5S',
  P5B = 'P5B',
}

export const gameTypesFromTicket = (ticketGameType: TicketGameType) => {
  switch (ticketGameType) {
    case TicketGameType.Pick2Boxed:
      return {
        pick: Pick.PICK2,
        type: TicketType.BOXED,
        gameType: GameType.PICK2,
        short: TicketGameTypeShort.P2B,
      };
    case TicketGameType.Pick2Straight:
      return {
        pick: Pick.PICK2,
        type: TicketType.STRAIGHT,
        gameType: GameType.PICK2,
        short: TicketGameTypeShort.P2S,
      };
    case TicketGameType.Pick3Boxed:
      return {
        pick: Pick.PICK3,
        type: TicketType.BOXED,
        gameType: GameType.PICK3,
        short: TicketGameTypeShort.P3B,
      };
    case TicketGameType.Pick3Straight:
      return {
        pick: Pick.PICK3,
        type: TicketType.STRAIGHT,
        gameType: GameType.PICK3,
        short: TicketGameTypeShort.P3S,
      };
    case TicketGameType.Pick4Boxed:
      return {
        pick: Pick.PICK4,
        type: TicketType.BOXED,
        gameType: GameType.PICK4,
        short: TicketGameTypeShort.P4B,
      };
    case TicketGameType.Pick4Straight:
      return {
        pick: Pick.PICK4,
        type: TicketType.STRAIGHT,
        gameType: GameType.PICK4,
        short: TicketGameTypeShort.P4S,
      };
    case TicketGameType.Pick5Boxed:
      return {
        pick: Pick.PICK5,
        type: TicketType.BOXED,
        gameType: GameType.PICK5,
        short: TicketGameTypeShort.P5B,
      };
    case TicketGameType.Pick5Straight:
      return {
        pick: Pick.PICK5,
        type: TicketType.STRAIGHT,
        gameType: GameType.PICK5,
        short: TicketGameTypeShort.P5S,
      };
  }
};

export const ticketGameTypesFromTypes = (pick: Pick, ticketType: TicketType) => {
  switch (pick) {
    case Pick.PICK2:
      return ticketType === TicketType.STRAIGHT
        ? { gameType: TicketGameType.Pick2Straight, short: TicketGameTypeShort.P2S }
        : { gameType: TicketGameType.Pick2Boxed, short: TicketGameTypeShort.P2B };
    case Pick.PICK3:
      return ticketType === TicketType.STRAIGHT
        ? { gameType: TicketGameType.Pick3Straight, short: TicketGameTypeShort.P3S }
        : { gameType: TicketGameType.Pick3Boxed, short: TicketGameTypeShort.P3B };
    case Pick.PICK4:
      return ticketType === TicketType.STRAIGHT
        ? { gameType: TicketGameType.Pick4Straight, short: TicketGameTypeShort.P4S }
        : { gameType: TicketGameType.Pick4Boxed, short: TicketGameTypeShort.P4B };
    case Pick.PICK5:
      return ticketType === TicketType.STRAIGHT
        ? { gameType: TicketGameType.Pick5Straight, short: TicketGameTypeShort.P5S }
        : { gameType: TicketGameType.Pick5Boxed, short: TicketGameTypeShort.P5B };
  }
};

const getMexicoToEasternOffsetHours = (): number => {
  const now = new Date();
  const mexico = new Date(now.toLocaleString('en-US', { timeZone: 'America/Mexico_City' }));
  const eastern = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return Math.round((eastern.getTime() - mexico.getTime()) / 3600000);
};

export const convertCentralToEastern = (hour: string): string => {
  const match = hour.match(/(\d{1,2}):(\d{2})\s?(AM|PM)/i);
  if (!match) return hour;
  const offset = getMexicoToEasternOffsetHours();
  let h = parseInt(match[1]);
  const m = match[2];
  let period = match[3].toUpperCase();
  // Convert to 24h
  let h24 = h;
  if (period === 'AM' && h24 === 12) h24 = 0;
  if (period === 'PM' && h24 !== 12) h24 += 12;
  // Apply offset
  h24 = (h24 + offset + 24) % 24;
  // Convert back to 12h
  period = h24 >= 12 ? 'PM' : 'AM';
  h = h24 % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${period}`;
};

export const toCamelCase = (str: string) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');
};
